import type { BlogPost } from "@shared/schema";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from "@/components/ui/carousel";

interface FeaturedPostsProps {
  posts: BlogPost[];
}

export function FeaturedPosts({ posts }: FeaturedPostsProps) {
  if (!posts.length) return null;

  return (
    <Carousel className="w-full">
      <CarouselContent>
        {posts.map((post) => (
          <CarouselItem key={post.id} className="md:basis-1/2 lg:basis-1/3">
            <FeaturedPostCard post={post} />
          </CarouselItem>
        ))}
      </CarouselContent>
      <div className="flex justify-end gap-2 mt-4">
        <CarouselPrevious />
        <CarouselNext />
      </div>
    </Carousel>
  );
}

interface FeaturedPostCardProps {
  post: BlogPost;
}

function FeaturedPostCard({ post }: FeaturedPostCardProps) {
  return (
    <Card className="h-full overflow-hidden transition-all hover:shadow-md">
      {/* Featured Image */}
      {post.imageUrl && (
        <div className="relative aspect-video w-full overflow-hidden">
          <img 
            src={post.imageUrl} 
            alt={post.title} 
            className="object-cover w-full h-full" 
          />
          <div className="absolute top-2 left-2">
            <Badge variant="secondary">Featured</Badge>
          </div>
        </div>
      )}
      
      {/* Content */}
      <CardHeader className="pb-2">
        <Badge variant="outline" className="w-fit mb-2">{post.category}</Badge>
        <h3 className="text-lg font-bold line-clamp-2">{post.title}</h3>
      </CardHeader>
      
      <CardContent className="pb-2">
        <p className="text-muted-foreground text-sm line-clamp-3">
          {post.summary}
        </p>
      </CardContent>
      
      <CardFooter className="flex justify-between">
        <div className="flex items-center text-xs text-muted-foreground">
          <CalendarIcon className="h-3 w-3 mr-1" />
          {new Date(post.publishedAt).toLocaleDateString("en-US", {
            month: "short", 
            day: "numeric"
          })}
        </div>
        
        <Button asChild variant="ghost" size="sm">
          <a href={`/blog/${post.slug}`}>
            Read <ChevronRight className="h-3 w-3 ml-1" />
          </a>
        </Button>
      </CardFooter>
    </Card>
  );
}